'use client';

/**
 * UpgradeCard — paywall affordance shown when `canUse(feature, user)` returns
 * `{ allowed: false, reason: 'plan' }`. Routes to /billing?upgrade=<plan>
 * with the plan label inferred from `FEATURE_MIN_PLAN`.
 *
 * Intentionally generic: drop it inside any client page that gates on a
 * feature, supply `feature` + a short description, and the card handles
 * the upgrade copy and CTA. The 4 pages that pre-date this component
 * (BulkClient, VoiceClient, RedlineClient, vault/versions) inline their
 * own card — they can migrate to this on a future cleanup pass without
 * changing UX.
 */
import { Sparkles } from 'lucide-react';
import { useRouter } from 'next/navigation';

import type { CanUseResult, Feature } from '@/lib/features';
import { FEATURE_MIN_PLAN, PLAN_LABEL } from '@/lib/features';

import { Button } from './Button';
import { Card } from './Card';

export interface UpgradeCardProps {
  /** The feature key the page is gated on. Drives the default upgrade target. */
  feature: Feature;
  /** Headline. Defaults to "<Feature display> is a Pro feature". */
  title?: string;
  /** One-line pitch under the headline. */
  description: string;
  /**
   * Optional `canUse()` result. When provided, the CTA target is read from
   * `result.upgradeTo` (so role/quota failures route correctly). When omitted,
   * we fall back to `FEATURE_MIN_PLAN[feature]`.
   */
  gate?: CanUseResult;
  /** Override CTA copy. Defaults to "Upgrade to <plan>". */
  ctaLabel?: string;
}

export function UpgradeCard({
  feature,
  title,
  description,
  gate,
  ctaLabel,
}: UpgradeCardProps) {
  const router = useRouter();

  const upgradeTo =
    gate && gate.allowed === false && gate.reason === 'plan'
      ? gate.upgradeTo
      : FEATURE_MIN_PLAN[feature];
  const planLabel = PLAN_LABEL[upgradeTo as keyof typeof PLAN_LABEL] ?? 'Pro';
  const heading = title ?? `${planLabel} feature`;

  return (
    <Card className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1 space-y-1">
        <h2 className="text-base font-semibold text-slate-900">{heading}</h2>
        <p className="text-sm text-slate-600">{description}</p>
      </div>
      <Button
        leftIcon={Sparkles}
        variant="accent"
        onClick={() =>
          router.push(`/billing?upgrade=${upgradeTo}&feature=${feature}`)
        }
      >
        {ctaLabel ?? `Upgrade to ${planLabel}`}
      </Button>
    </Card>
  );
}
